import {
  AccountTypeEnum,
  TransferDestinationInput,
  TransferDestinationTypeInput,
  TransferSourceInput,
} from '@varo/graphql-schema-types';
import { useLocalSearchParams } from 'expo-router';
import {
  TransferDestinationOption,
  TransferSourceOption,
} from 'features/feeBasedInstantTransfer/adapters/transferOptionsAdapter';
import {
  createTransferPreselectionIntent,
  EXTERNAL_DEBIT_ACCOUNT_TYPE,
  TransferPreselectionRouteParams,
  TransferPreselectionSource,
} from 'features/feeBasedInstantTransfer/helpers/transferPreselection';
import { useEffect, useState } from 'react';

interface TransferPreselectionOptions {
  sourceOptions: readonly TransferSourceOption[];
  destinationOptions: readonly TransferDestinationOption[];
  isSourceLoading?: boolean;
  isDestinationLoading?: boolean;
  hasSourceError?: boolean;
  hasDestinationError?: boolean;
  source?: TransferSourceInput;
}

interface TransferPreselectedOptions {
  source?: TransferSourceInput;
  destination?: TransferDestinationInput;
  amount?: string;
  targetAmount?: string;
}

interface TransferPreselectionState {
  hasResolvedDestination: boolean;
  options?: TransferPreselectedOptions;
}

function findSourceOption(
  sourceOptions: readonly TransferSourceOption[],
  sourceIntent: TransferPreselectionSource | undefined,
) {
  if (!sourceIntent) {
    return undefined;
  }

  return sourceIntent.id
    ? sourceOptions.find(
        (option) => option.id === sourceIntent.id && option.isSelectable,
      )
    : sourceOptions.find(
        (option) =>
          option.isSelectable &&
          option.accountType === AccountTypeEnum.Internal &&
          option.accountProduct === sourceIntent.type,
      );
}

function findDestinationOption(
  destinationOptions: readonly TransferDestinationOption[],
  destinationIntent: ReturnType<
    typeof createTransferPreselectionIntent
  >['destination'],
) {
  if (!destinationIntent) {
    return undefined;
  }

  if (destinationIntent.id) {
    return destinationOptions.find(
      (option) => option.id === destinationIntent.id && option.isSelectable,
    );
  }

  if (destinationIntent.type === EXTERNAL_DEBIT_ACCOUNT_TYPE) {
    return destinationOptions.find(
      (option) =>
        option.isSelectable &&
        option.input.type === TransferDestinationTypeInput.ExternalDebitCard,
    );
  }

  return destinationOptions.find(
    (option) =>
      option.isSelectable &&
      option.kind === 'account' &&
      option.accountType === AccountTypeEnum.Internal &&
      option.accountProduct === destinationIntent.type,
  );
}

export function useTransferPreselection({
  sourceOptions,
  destinationOptions,
  isSourceLoading = false,
  isDestinationLoading = false,
  hasSourceError = false,
  hasDestinationError = false,
  source,
}: TransferPreselectionOptions) {
  const routeParams = useLocalSearchParams<TransferPreselectionRouteParams>();
  const [preselectionIntent] = useState(() =>
    createTransferPreselectionIntent(routeParams),
  );
  const {
    amount: preselectedAmount,
    targetAmount: preselectedTargetAmount,
    destination: preselectedDestination,
    source: preselectedSource,
    isAccountInteractionDisallowed = false,
  } = preselectionIntent;
  const hasTransferPreselection = Boolean(
    preselectedAmount ||
    preselectedTargetAmount ||
    preselectedDestination ||
    preselectedSource,
  );
  const [transferPreselectionState, setTransferPreselectionState] =
    useState<TransferPreselectionState>({
      hasResolvedDestination: false,
    });

  useEffect(() => {
    if (
      !hasTransferPreselection ||
      transferPreselectionState.options !== undefined ||
      (preselectedSource && (isSourceLoading || hasSourceError))
    ) {
      return;
    }

    const selectedSource = findSourceOption(sourceOptions, preselectedSource);

    setTransferPreselectionState((currentState) => ({
      ...currentState,
      options: {
        ...(selectedSource ? { source: selectedSource.input } : {}),
        ...(preselectedAmount ? { amount: preselectedAmount } : {}),
        ...(preselectedTargetAmount
          ? { targetAmount: preselectedTargetAmount }
          : {}),
      },
    }));
  }, [
    preselectedAmount,
    preselectedTargetAmount,
    preselectedSource,
    hasTransferPreselection,
    hasSourceError,
    isSourceLoading,
    sourceOptions,
    transferPreselectionState.options,
  ]);

  useEffect(() => {
    if (
      transferPreselectionState.hasResolvedDestination ||
      !preselectedDestination ||
      hasDestinationError
    ) {
      return;
    }

    if (!source) {
      if (
        transferPreselectionState.options !== undefined &&
        !transferPreselectionState.options?.source
      ) {
        setTransferPreselectionState((currentState) => ({
          ...currentState,
          hasResolvedDestination: true,
        }));
      }
      return;
    }
    if (isDestinationLoading) {
      return;
    }

    const selectedDestination = findDestinationOption(
      destinationOptions,
      preselectedDestination,
    );
    setTransferPreselectionState((currentState) => ({
      ...currentState,
      hasResolvedDestination: true,
      options: {
        ...currentState.options,
        ...(selectedDestination && selectedDestination.id !== source.id
          ? {
              destination: selectedDestination.input,
            }
          : {}),
      },
    }));
  }, [
    destinationOptions,
    hasDestinationError,
    isDestinationLoading,
    preselectedDestination,
    source,
    transferPreselectionState.hasResolvedDestination,
    transferPreselectionState.options,
  ]);

  return {
    isTransferPreselectionPending:
      hasTransferPreselection &&
      !hasSourceError &&
      !hasDestinationError &&
      (transferPreselectionState.options === undefined ||
        (Boolean(preselectedDestination) &&
          !transferPreselectionState.hasResolvedDestination)),
    transferPreselectedOptions: transferPreselectionState.options,
    isAccountInteractionDisallowed,
  };
}
